import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import DatePicker from 'react-datepicker';
import Select from 'react-select';
import './AuctionList.css';
import { API_BASE_URL } from '../../config/constant';
import MasterService from '../../services/MasterService';

const AuctionList = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('live');
    const [auctions, setAuctions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0,
        hasNext: false,
        hasPrev: false
    });
    const [winCount, setWinCount] = useState(0);
    const [watchlistCount, setWatchlistCount] = useState(0);
    const [liveCount, setLiveCount] = useState(0);
    const [upcomingCount, setUpcomingCount] = useState(0);
    const [expiredCount, setExpiredCount] = useState(0);
    const [cities, setCities] = useState([]);
    const [selectedCity, setSelectedCity] = useState('');
    const [startDate, setStartDate] = useState('');

    const tabs = [
        { id: 'live', label: 'Live', type: 'live', count: liveCount },
        { id: 'upcoming', label: 'Upcoming', type: 'upcoming', count: upcomingCount },
        { id: 'expired', label: 'Expired', type: 'expired', count: expiredCount }
    ];

    useEffect(() => {
        fetchCities();
    }, []);

    useEffect(() => {
        fetchAuctionList();
    }, [activeTab]);

    const fetchAuctionList = async (page = 1) => {
        fetchAuctionListWithFilters(page, selectedCity, startDate);
    };

    const fetchAuctionListWithFilters = async (page = 1, cityOverride = null, dateOverride = null) => {
        setLoading(true);
        try {
            const currentTab = tabs.find(tab => tab.id === activeTab);
            const requestBody = {
                source: 'web',
                type: currentTab.type,
                page: page
            };

            // Use override values if provided, otherwise use state values
            const cityFilter = cityOverride !== null ? cityOverride : selectedCity;
            const dateFilter = dateOverride !== null ? dateOverride : startDate;

            // Add city_id filter if selected
            if (cityFilter) {
                requestBody.city_id = parseInt(cityFilter);
            }

            // Add start_date filter if provided
            if (dateFilter) {
                requestBody.start_date = dateFilter;
            }

            const response = await fetch(`${API_BASE_URL}/auction/auction-list`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'apikey': 'd77bc90f-3350-47da-94c2-2930de73difauction'
                },
                body: JSON.stringify(requestBody)
            });

            const result = await response.json();
            
            if (result.status === 200) {
                setAuctions(result.data.auctions);
                setPagination(result.data.pagination);
                setWinCount(result.data.win_count);
                setWatchlistCount(result.data.watchlist_count);
                setLiveCount(result.data.live_count);
                setUpcomingCount(result.data.upcoming_count);
                setExpiredCount(result.data.expired_count);
            } else {
                toast.error('Failed to fetch auction list');
            }
        } catch (error) {
            console.error('Error fetching auction list:', error);
            toast.error('Failed to fetch auction list');
        } finally {
            setLoading(false);
        }
    };

    const fetchCities = async () => {
        try {
            const response = await MasterService.getCities();
            
            if (response.status === 200 && response.data) {
                setCities(response.data.data.city);
            }
        } catch (error) {
            console.error('Error fetching cities:', error);
            toast.error('Failed to fetch cities');
        }
    };

    const handleTabClick = (tabId) => {
        setActiveTab(tabId);
        setStartDate(''); // Clear start date when tab changes
    };

    const handlePageChange = (page) => {
        fetchAuctionList(page);
    };

    const handleSearch = () => {
        fetchAuctionListWithFilters(1, selectedCity, startDate);
    };

    const handleReset = () => {
        setSelectedCity('');
        setStartDate('');
        // Pass empty values directly to avoid state timing issues
        fetchAuctionListWithFilters(1, '', '');
    };

    const getMinDate = () => {
        if (activeTab === 'upcoming') {
            // For upcoming tab, only allow future dates
            const today = new Date();
            today.setDate(today.getDate()); // Today onwards
            return today.toISOString().split('T')[0];
        }
        return ''; // No restriction for other tabs
    };

    const getMaxDate = () => {
        if (activeTab === 'expired') {
            // For expired tab, only allow past dates
            const today = new Date();
            today.setDate(today.getDate() - 1); // Yesterday backwards
            return today.toISOString().split('T')[0];
        }
        return ''; // No restriction for other tabs
    };

    const handleViewInventory = (auctionId) => {
        navigate(`/auction/${auctionId}/inventory`);
    };

    const handleEditAuction = (auctionId) => {
        navigate(`/auction/edit/${auctionId}`);
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <div className="auction-list-container">
            {/* Header */}
            <div className="auction-header auction-list-heading">
                <h1>Auction Management</h1>
                <div className="header-actions">
                    <button 
                        className="btn-line"
                        onClick={() => navigate('/auction/create')}
                    >
                        Add Auction
                    </button>
                    {/* <div className="header-stats">
                        <div className="stat-item">
                            <span className="stat-label">Wins</span>
                            <span className="stat-value">{winCount}</span>
                        </div>
                        <div className="stat-item">
                            <span className="stat-label">Watchlist</span>
                            <span className="stat-value">{watchlistCount}</span>
                        </div>
                    </div> */}
                </div>
            </div>

            {/* Filters */}
            <div className="auction-filters-section">
                <div className="auction-filters-container">
                    <div className="filter-group">
                        <label htmlFor="city-filter" className="filter-label">City:</label>
                        <Select
                            id="city-filter"
                            // className="filter-select"
                            value={selectedCity ? { value: selectedCity, label: cities.find(c => c.id.toString() === selectedCity)?.name || selectedCity } : null}
                            onChange={(selectedOption) => setSelectedCity(selectedOption ? selectedOption.value : '')}
                            options={[
                                { value: '', label: 'All Cities' },
                                ...cities.map(city => ({ value: city.id.toString(), label: city.name }))
                            ]}
                            placeholder="Select City"
                            isClearable
                            isSearchable
                        />
                    </div>
                    <div className="filter-group">
                        <label htmlFor="start-date-filter" className="filter-label">Start Date:</label>
                        <DatePicker
                            id="start-date-filter"
                            selected={startDate ? new Date(startDate) : null}
                            onChange={(date) => {
                                const formattedDate = date ? date.toLocaleDateString('en-CA') : '';
                                setStartDate(formattedDate);
                            }}
                            dateFormat="dd/MM/yyyy"
                            placeholderText="Select Start Date"
                            className="filter-input"
                            minDate={getMinDate() ? new Date(getMinDate()) : undefined}
                            maxDate={getMaxDate() ? new Date(getMaxDate()) : undefined}
                        />
                    </div>
                    <div className="filter-group">
                        <button 
                            className="btn-primary"
                            onClick={handleSearch}
                        >
                            Search
                        </button>
                    </div>
                    <div className="filter-group">
                        <button 
                            className="btn-line"
                            onClick={handleReset}
                        >
                            Reset
                        </button>
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className="auction-tabs-section">
                <div className="auction-tabs-container">
                    {tabs.map((tab) => (
                        <button
                            key={tab.id}
                            className={`auction-tab-button ${activeTab === tab.id ? 'active' : ''}`}
                            onClick={() => handleTabClick(tab.id)}
                        >
                            {tab.label} ({tab.count})
                        </button>
                    ))}
                </div>
            </div>

            {/* Table */}
            <div className="auction-table-section">
                <div className="auction-table-container">
                    {loading ? (
                        <div className="auction-loading-container">
                            <div className="auction-loading-spinner">Loading...</div>
                        </div>
                    ) : (
                        <table className="auctions-table">
                            <thead>
                                <tr>
                                    {activeTab === 'upcoming' ? (
                                        <>
                                            <th>Auction ID</th>
                                            <th>Auction Name</th>
                                            <th>Number of Units</th>
                                            <th>Auction Start Date<br/>& Time</th>
                                            <th>Auction End Date<br/>& Time</th>
                                            <th>Location</th>
                                            <th>Number of Dealers<br/>Eligible for Buying</th>
                                            <th>Action</th>
                                        </>
                                    ) : (
                                        <>
                                            <th>Auction ID</th>
                                            <th>Auction Name</th>
                                            <th>Number of Units</th>
                                            <th>Auction Start Date<br/>& Time</th>
                                            <th>Auction End Date<br/>& Time</th>
                                            <th>Location</th>
                                            <th>Unit Received Bid</th>
                                            <th>No of Bidders</th>
                                            <th>No of Dealers<br/>Eligible for Buying</th>
                                            <th>View Auction<br/>Inventory</th>
                                        </>
                                    )}
                                </tr>
                            </thead>
                            <tbody>
                                {auctions.length > 0 ? (
                                    auctions.map((auction) => (
                                        <tr key={auction.auction_id}>
                                            {activeTab === 'upcoming' ? (
                                                <>
                                                    <td>{auction.auction_id}</td>
                                                    <td>{auction.auction_name}</td>
                                                    <td>{auction.number_of_units}</td>
                                                    <td>{formatDate(auction.auction_start_date_time)}</td>
                                                    <td>{formatDate(auction.auction_end_date_time)}</td>
                                                    <td>{auction.location}</td>
                                                    <td>{auction.number_of_dealers_eligible_for_buying}</td>
                                                    <td>
                                                        <button 
                                                            className="edit-auction-btn"
                                                            onClick={() => handleEditAuction(auction.auction_id)}
                                                            title="Edit Auction"
                                                        >
                                                            Edit
                                                        </button>
                                                    </td>
                                                </>
                                            ) : (
                                                <>
                                                    <td>{auction.auction_id}</td>
                                                    <td>{auction.auction_name}</td>
                                                    <td>{auction.number_of_units}</td>
                                                    <td>{formatDate(auction.auction_start_date_time)}</td>
                                                    <td>{formatDate(auction.auction_end_date_time)}</td>
                                                    <td>{auction.location}</td>
                                                    <td>{auction.unit_received_bid}</td>
                                                    <td>{auction.no_of_bidders}</td>
                                                    <td>{auction.number_of_dealers_eligible_for_buying}</td>
                                                    <td>
                                                        <button 
                                                            className="btn-line"
                                                            onClick={() => handleViewInventory(auction.auction_id)}
                                                        >
                                                            View 
                                                        </button>
                                                    </td>
                                                </>
                                            )}
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={activeTab === 'upcoming' ? '7' : '10'} className="auction-no-data">
                                            No auctions found for this category
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {/* Pagination */}
            <div className="auction-pagination-section">
                <div className="auction-pagination-container">
                    <button 
                        className="auction-pagination-btn" 
                        disabled={!pagination.hasPrev}
                        onClick={() => handlePageChange(pagination.page - 1)}
                    >
                        Previous
                    </button>
                    <span className="auction-page-info">
                        Page {pagination.page} of {pagination.totalPages} 
                        ({pagination.total} total auctions)
                    </span>
                    <button 
                        className="auction-pagination-btn" 
                        disabled={!pagination.hasNext}
                        onClick={() => handlePageChange(pagination.page + 1)}
                    >
                        Next
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AuctionList;
